import { IconButton } from '@chakra-ui/react';
import { MaterialIcon } from '../ui/MaterialIcon';
import { Tooltip } from '../ui/Tooltip';

/**
 * The chrome buttons in a left-column panel header — Bower's history, new chat
 * and collapse, the workspace sidebar's collapse.
 *
 * One shape, because they are one row: they had drifted to `size="lg"` with
 * 20px glyphs, which put three 48px targets beside a 17px title and made the
 * header read as a toolbar rather than a label with affordances.
 *
 * 32px, which clears WCAG 2.5.8's 24x24 but is under 2.5.5's AAA 44x44 — the
 * same trade the command palette's filters button takes, and for the same
 * reason: the design draws a 30px control here and every one of these has a
 * keyboard route. Flagged rather than hidden; see the palette's test for the
 * full argument.
 *
 * Shared rather than copied because the three panels take turns in ONE column
 * (`LeftPanelColumn`): a different size or hover treatment between them reads
 * as a glitch at the moment you switch, which is exactly when both are on
 * screen in the user's memory.
 */
export const PANEL_HEADER_BUTTON = {
  size: 'sm',
  variant: 'ghost',
  color: 'text.muted',
  borderRadius: 'lg',
  boxSize: '32px',
  minW: '32px',
  _hover: { bg: 'bg.hover', color: 'text.primary' },
} as const;

interface PanelCollapseButtonProps {
  /** Names the panel being collapsed, e.g. "Collapse workspace panel". */
  readonly label: string;
  /** Vacates the slot — the column's `onClose`. */
  readonly onClick: () => void;
}

/**
 * The collapse affordance every left-column panel carries in its header.
 *
 * `keyboard_double_arrow_left` because the gesture is "put this back on the
 * rail", not "dismiss this": the panel folds left into the nav item that opened
 * it, and the rail item stays lit as the way back. A close X would claim the
 * panel is gone.
 *
 * The Tooltip is BARE — no `hasArrow`, no `placement`. Bird's header is the one
 * place this button has tooltip neighbours (history, new conversation) and both
 * of those are `<Tooltip label="…">`, so an arrow here would give the collapse
 * control a treatment the two buttons 32px to its left do not have. That is the
 * argument for sharing `PANEL_HEADER_BUTTON`, pointed the other way. Chakra's
 * default placement is already `bottom`, so stating it bought nothing.
 */
export function PanelCollapseButton({ label, onClick }: PanelCollapseButtonProps) {
  return (
    <Tooltip label={label}>
      <IconButton
        aria-label={label}
        icon={<MaterialIcon name="keyboard_double_arrow_left" boxSize={4} />}
        onClick={onClick}
        // Kept, unlike `hasArrow`: the workspace sidebar's heading beside this
        // is a USER-SUPPLIED workspace name, and a long one competing for the
        // row is a real case there rather than a hypothetical. Its `minW={0}`
        // is what should yield, not this fixed-size control.
        flexShrink={0}
        {...PANEL_HEADER_BUTTON}
      />
    </Tooltip>
  );
}
